/**
 * `fetch` for a hub address, in a shape Electron's URL parser cannot bend.
 *
 * Every hub call in Electron goes through the renderer's fetch bridge
 * (`src/main.ts` → `rewriteElectronLocalRequestUrl` turns an absolute hub URL
 * into `otto-local://<host>:<port>/…`, `electron/main.ts` walks its transport
 * ladder). Chromium parses that URL on the way through, and for a scheme it
 * does not treat as special it **drops the port**:
 *
 *     new URL('otto-local://192.168.1.2:16162/api/hub/adopt').port
 *       → '' (measured in the renderer, Chromium; Node's parser says '16162')
 *
 * The ladder reads the port from that URL (`localProtocolExplicitPort`), and
 * its "explicit port" branch is what keeps a request pointed at the address the
 * caller named. With the port gone the request falls into the portless ladder
 * (443 / 16161 / 80) plus the host's remembered winner — so a hub at a
 * non-default port is answered by whichever hub on that host last answered, and
 * the app talks to the wrong box while every string it printed said otherwise.
 *
 * A hub call that names its own address therefore travels the IPC bridge with
 * the URL as a **string** (`window.electron.httpRequest` — the transport the
 * app's axios layer already uses): no URL parse happens on the way, the port
 * survives, and the ladder lands on the hub the caller asked for. Everywhere
 * else (`local`, `cloud`, a plain browser, Capacitor) a hub is reached with
 * plain `fetch`, and that is what this returns.
 *
 * **The rest of the app's `fetch` calls cannot take that route** — they are not
 * this module's callers, they are `fetch(hubUrl)` anywhere in the tree (a
 * relative `/api/…`, a hand-built `http://<ip>:<port>/api/…`, an
 * `otto-local://…` string), and the renderer's fetch bridge (`src/main.ts` →
 * `installElectronLocalFetchBridge`) is the ONE place they all pass through.
 * Routing them over IPC instead is not an option: the bridge carries a string
 * body, so a multipart upload would lose its bytes. So the bridge *moves the
 * port out of the authority* — where Chromium's parser drops it — and into the
 * path, which the parser keeps:
 *
 *     otto-local://192.168.1.2:16162/api/hub/identity
 *       → otto-local://192.168.1.2/__otto_local_port/16162/api/hub/identity
 *
 * `electron/localProtocolLadder.ts` reads that marker back
 * (`localProtocolMarkedPort`) and honours it as the explicit port the docstring
 * there promises, then strips it before any target URL is built — so the hub
 * never sees the marker. `withHubPortMarker` is the encode half of that pair;
 * the token is mirrored in the ladder, and the test that both halves agree is
 * `localProtocolLadder.test.ts`.
 *
 * What the synthesized `Response` keeps is exactly what the callers read:
 * `status`, `ok`, `headers`, `json()`, `text()`. The bridge's `status: 0`
 * answer (the main process reporting a transport failure) is thrown as the
 * network error every caller's `catch` already treats it as.
 */
import { rewriteElectronLocalRequestUrl } from '../config/runtime';

/**
 * The path segment the fetch bridge carries a hub's port in.
 *
 * Mirrored in `electron/localProtocolLadder.ts` (`LOCAL_PORT_MARKER`): the
 * electron build compiles with its own rootDir and cannot import this module,
 * so the two copies exist and are asserted equal by `localProtocolLadder.test.ts`.
 */
export const HUB_PORT_MARKER = '__otto_local_port';

/** `otto-local://` with an explicit port in the authority: the shape Chromium mangles. */
const OTTO_LOCAL_AUTHORITY_PORT = /^(otto-local:\/\/)(\[[^\]]+\]|[^/:?#]+):(\d+)(?=[/?#]|$)/i;

/**
 * The same `otto-local://` URL with its port moved into the path — the one shape
 * Chromium's URL parser cannot bend.
 *
 * Only URLs whose authority names a port are touched; anything else (a portless
 * address, another scheme, a marked URL already) is returned unchanged, so this
 * is safe to apply twice and safe to apply to a URL that was never mangled.
 */
export function withHubPortMarker(url: string): string {
  const match = OTTO_LOCAL_AUTHORITY_PORT.exec(url);
  if (!match) return url;
  const [, scheme, host, port] = match;
  const rest = url.slice(match[0].length);
  return `${scheme}${host}/${HUB_PORT_MARKER}/${port}${rest}`;
}

/**
 * What the Electron fetch bridge rewrites a request to: the hub rewrite
 * (`config/runtime`) followed by the port marker.
 *
 * One function so the bridge in `src/main.ts` and the ladder cannot disagree
 * about the shape — `localProtocolLadder.test.ts` runs this chain and feeds its
 * output to the ladder.
 */
export function rewriteElectronHubFetchUrl(url: string): string {
  return withHubPortMarker(rewriteElectronLocalRequestUrl(url));
}

/** One answer from `electron:http-request` (see `electron/main.ts`). */
export interface ElectronHttpAnswer {
  status: number;
  statusText?: string;
  /** Parsed body for textual answers. */
  data?: unknown;
  /** Raw body; base64 when `bodyEncoding` says so. */
  body?: string;
  bodyEncoding?: 'text' | 'base64';
  headers?: Record<string, string>;
  url?: string;
}

export interface ElectronHttpBridge {
  httpRequest(options: {
    url: string;
    method?: string;
    headers?: Record<string, string>;
    body?: string;
  }): Promise<ElectronHttpAnswer>;
}

/** What every caller of this module uses — `fetch`'s own shape. */
export type HubFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/** The bridge, when this window has one (Electron preload, `electron/preload.ts`). */
export function electronHttpBridge(): ElectronHttpBridge | null {
  const api = (globalThis as { window?: { electron?: ElectronHttpBridge } }).window?.electron;
  return api && typeof api.httpRequest === 'function' ? api : null;
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

function headerRecord(headers: HeadersInit | undefined): Record<string, string> {
  const record: Record<string, string> = {};
  if (!headers) return record;
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    headers.forEach((value, key) => {
      record[key] = value;
    });
    return record;
  }
  if (Array.isArray(headers)) {
    for (const [key, value] of headers) record[key] = value;
    return record;
  }
  for (const [key, value] of Object.entries(headers)) {
    if (typeof value === 'string') record[key] = value;
  }
  return record;
}

function decodeBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** The IPC answer, as a `Response`. Throws for the bridge's `status: 0`. */
export function answerToResponse(answer: ElectronHttpAnswer): Response {
  if (!answer || typeof answer.status !== 'number' || answer.status < 200) {
    throw new Error(`Hub request failed: ${answer?.statusText || 'no answer'}`);
  }

  const headers = new Headers(answer.headers ?? {});
  let body: BodyInit | null = null;
  if (answer.bodyEncoding === 'base64') {
    // `Response` takes a Blob as a body; a bare Uint8Array is not a `BodyInit`
    // under the DOM lib's types.
    body = new Blob([decodeBase64(answer.body ?? '')]);
  } else if (typeof answer.body === 'string') {
    body = answer.body;
  } else if (answer.data !== undefined && answer.data !== null) {
    body = typeof answer.data === 'string' ? answer.data : JSON.stringify(answer.data);
  }

  return new Response(body, {
    status: answer.status,
    statusText: answer.statusText ?? '',
    headers,
  });
}

/**
 * The transport to use for a hub URL: the IPC bridge when there is one, plain
 * `fetch` otherwise.
 *
 * `bridge`/`fetchImpl` are injected by tests. The abort signal is honoured on
 * both paths — a caller's timeout keeps meaning "stop waiting and try the next
 * address shape", even though an IPC call cannot be cancelled mid-flight.
 */
export function resolveHubFetch(
  bridge: ElectronHttpBridge | null = electronHttpBridge(),
  fetchImpl: HubFetch | null = typeof fetch === 'function' ? (fetch as HubFetch) : null,
): HubFetch {
  if (!bridge) {
    return (input, init) => {
      if (!fetchImpl) {
        return Promise.reject(new Error('No fetch available in this runtime'));
      }
      return fetchImpl(input, init);
    };
  }

  return async (input, init) => {
    const target = rewriteElectronLocalRequestUrl(urlOf(input));
    const method = (init?.method ?? 'GET').toUpperCase();
    const body = typeof init?.body === 'string' ? init.body : undefined;

    const pending = bridge
      .httpRequest({ url: target, method, headers: headerRecord(init?.headers), body })
      .then(answerToResponse);

    const signal = init?.signal ?? null;
    if (!signal) return pending;
    if (signal.aborted) throw new Error('Hub request aborted');

    return new Promise<Response>((resolve, reject) => {
      const onAbort = () => reject(new Error('Hub request aborted'));
      signal.addEventListener('abort', onAbort, { once: true });
      pending.then(
        (response) => {
          signal.removeEventListener('abort', onAbort);
          resolve(response);
        },
        (error) => {
          signal.removeEventListener('abort', onAbort);
          reject(error);
        },
      );
    });
  };
}
